import { useState } from 'react';
import { Link } from 'react-router';
import { publicForms, type PublicForm } from '../../public/publicRoutes';

/**
 * Where the public forms' links are found, so nobody has to remember a URL or dig one out of a
 * previous email.
 *
 * Named "CT Interview Forms (Public)" and filed under Recruiting, since both forms it hands out
 * are recruiting's. The folder, section id and path keep the older "public-forms" spelling: the id
 * is what `/access` would be keyed by and the path is what anyone who bookmarked this page has.
 *
 * This is a signed-in page *about* the public pages, not one of them. It reaches no API — the list
 * comes from `publicRoutes.ts`, the same module that declares the routes — which is why its registry
 * entry is `clientOnly` and no backend module exists for it. Nothing here is gated, because every
 * URL on it is public by definition: anyone on the internet can open these pages, and that is the
 * decision recorded in `docs/architecture.md`, not a gap this page could close by hiding them.
 *
 * Note the section's own path is `/public-forms`, which is deliberately *not* under the `/public/`
 * routing prefix — `isPublicPath` tests for `/public` exactly or a `/public/` prefix, so this page
 * stays behind sign-in where it belongs.
 */
export function PublicFormsSection() {
  return (
    <div className="care-page">
      <header className="care-page__header">
        <h1 className="care-page__title">CT Interview Forms (Public)</h1>
        <p className="care-page__subtitle">
          Links to the forms that need no sign-in. Open one on a front-desk machine or a tablet, or
          copy the link and send it to whoever has to fill it in.
        </p>
      </header>

      {/* Said plainly and once, at the top. Somebody about to paste one of these into an email is
          the person who most needs to know that the link is not a private one. */}
      <div className="public-forms__notice">
        <p>
          <strong>These links are public.</strong> Anyone who opens one can fill the form in — there
          is no sign-in and no restriction on where it is opened from. That is by design: the people
          filling these in have no account here. What they submit is only readable from the hub.
        </p>
      </div>

      <div className="public-forms">
        {publicForms.map((form) => (
          <PublicFormCard key={form.id} form={form} />
        ))}
      </div>
    </div>
  );
}

function PublicFormCard({ form }: { form: PublicForm }) {
  // Absolute, because the whole point is a link somebody can paste somewhere else. Read from the
  // browser rather than configured, so it is right in every environment without a build-time value
  // that would be wrong in one of them.
  const url = `${window.location.origin}${form.path}`;

  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  async function copy() {
    setCopyError(null);

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);

      // Reverts on its own. A button stuck on "Copied" is a button that looks broken the next time
      // somebody comes back to it.
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error: unknown) {
      console.error('Could not copy the link to the clipboard', error);

      // The URL is on screen and selectable, so a refused clipboard costs nothing but a manual copy.
      setCopyError('The browser would not write to the clipboard — select the link and copy it.');
    }
  }

  return (
    <section className="public-forms__card">
      <h2 className="public-forms__title">{form.label}</h2>
      <p className="public-forms__description">{form.description}</p>

      {/* Shown in full rather than hidden behind the button: somebody checking they are about to
          send the right link should be able to read it. */}
      <p className="public-forms__url">{url}</p>

      <div className="public-forms__actions">
        <a
          className="button button--primary"
          href={form.path}
          target="_blank"
          rel="noreferrer"
        >
          Open
        </a>

        <button type="button" className="button button--quiet" onClick={() => void copy()}>
          {copied ? 'Link copied' : 'Copy link'}
        </button>

        {form.dashboardPath !== null && (
          <Link className="public-forms__dashboard" to={form.dashboardPath}>
            See what has been submitted
          </Link>
        )}
      </div>

      {copyError && (
        <p className="field__error" role="alert">
          {copyError}
        </p>
      )}
    </section>
  );
}
